import { useThrottle } from '@chronosphereio/shared';
import { connect, ECElementEvent, ECharts, EChartsCoreOption, init, Payload } from 'echarts/core';
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { getMockEChart } from '@/components/mock-components/mock-echart-state';
import { ContentDimensions } from '../../core';
import { shouldDebugOptionUpdates } from '../../core/utils/env';
import { throwDevError } from '../../core/utils/logging';
import { EChartsTheme } from '../model';
import {
  BatchEventName,
  ChartEventName,
  debugOptionUpdateArgs,
  EventName,
  generateOptionUpdateArgs,
  isEChartBatchEvent,
  isEChartMouseEvent,
  ModelFinder,
  MouseEventName,
} from './model';

// see docs for info about each property: https://echarts.apache.org/en/api.html#events
export interface MouseEventsParameters<SeriesData> extends ECElementEvent {
  data: Record<string, unknown> & SeriesData;
}

type OnEventFunction<T> = (
  params: MouseEventsParameters<T>,
  // This is potentially undefined for testing purposes
  instance?: ECharts
) => void;

// batch event types
export interface DataZoomPayloadBatchItem {
  dataZoomId: string;
  // start and end not returned unless dataZoom is based on percentProp,
  // which is for cases when a dataZoom component controls multiple axes
  start?: number;
  end?: number;
  // startValue and endValue return data index for 'category' axes,
  // for axis types 'value' and 'time', actual values are returned
  startValue?: number;
  endValue?: number;
}

export interface HighlightPayloadBatchItem {
  dataIndex: number;
  dataIndexInside: number;
  seriesIndex: number;
  // highlight action can effect multiple connected charts
  escapeConnect?: boolean;
  // whether blur state was triggered
  notBlur?: boolean;
}

export interface BatchEventsParameters {
  type: BatchEventName;
  batch: DataZoomPayloadBatchItem[] & HighlightPayloadBatchItem[];
}

export type OnBatchEventFunction = (params: BatchEventsParameters) => void;

export type OnEventsType<SeriesData> = {
  [mouseEventName in MouseEventName]?: OnEventFunction<SeriesData>;
} & {
  [batchEventName in BatchEventName]?: OnBatchEventFunction;
} & {
  [eventName in ChartEventName]?: () => void;
};

export interface ChartApi {
  /**
   * Get the html element that contains the chart.
   */
  getContainerEl: () => HTMLElement | null;
  /**
   * Get the ECharts model
   */
  getChartModel: () => ECharts['_model'] | null;
  /**
   * Triggers chart action
   * https://echarts.apache.org/en/api.html#echartsInstance.dispatchAction
   */
  dispatchAction: (payload: Payload) => void;
  /**
   * Convert a point from pixel coordinate to logical coordinate.
   * https://echarts.apache.org/en/api.html#echartsInstance.convertFromPixel
   */
  convertFromPixel: (finder: ModelFinder, value: number[]) => number[] | undefined;
  /**
   * Determine whether the given point is in the given coordinate systems or series.
   * https://echarts.apache.org/en/api.html#echartsInstance.containPixel
   */
  containPixel: (finder: ModelFinder, value: number[]) => boolean | undefined;
  /**
   * Convert a point from logical coordinate to pixel coordinate.
   * https://echarts.apache.org/en/api.html#echartsInstance.convertToPixel
   */
  convertToPixel: (finder: ModelFinder, value: number[]) => number[] | undefined;
  /**
   * Shows loading animation
   * @deprecated We should handle loading state in VizPanel
   */
  showLoading: () => void;
  /**
   * Hides loading animation
   * @deprecated We should handle loading state in VizPanel
   */
  hideLoading: () => void;
  /**
   * Binds event-handling function
   */
  on: (eventName: string, handler: (args: unknown) => void) => void;
  /**
   * Resizes the chart
   */
  resize: () => void;
}

export interface EChartsProps<SeriesData> extends ContentDimensions {
  option: EChartsCoreOption;
  theme?: string | EChartsTheme;
  renderer?: 'canvas' | 'svg';
  /**
   * Event handler for when the mouse hovers over a chart element
   * Use `useCallback` to prevent unnecessary re-renders and avoid repeatedly adding/removing event listeners.
   */
  onMouseover?: OnEventFunction<SeriesData>;
  /**
   * Event handler for when the mouse leaves a previously hovered chart element
   * Use `useCallback` to prevent unnecessary re-renders and avoid repeatedly adding/removing event listeners.
   */
  onMouseout?: OnEventFunction<SeriesData>;
  /**
   * Event handler for when a chart element is clicked
   * Use `useCallback` to prevent unnecessary re-renders and avoid repeatedly adding/removing event listeners.
   */
  onClick?: OnEventFunction<SeriesData>;
  /**
   * Event handler for when the user performs a data zoom interaction
   * Use `useCallback` to prevent unnecessary re-renders and avoid repeatedly adding/removing event listeners.
   * https://echarts.apache.org/en/api.html#events.datazoom
   */
  onDataZoom?: OnBatchEventFunction;
  /**
   * Event handler that is called after all rendering and animations are fully completed.
   * Use `useCallback` to prevent unnecessary re-renders and avoid repeatedly adding/removing event listeners.
   * https://echarts.apache.org/en/api.html#events.finished
   */
  onFinished?: () => void;
  /**
   * Ref to the echarts instance. Use with caution because it can be easy to
   * accidentally cause memory leaks when used in react code.
   * See related: https://schiener.io/2024-03-03/react-closures
   *
   * @deprecated use APIs provided by the `chartApiRef` prop
   */
  _instance?: React.MutableRefObject<ECharts | undefined>;
  /**
   * Ref to an imperative api for interacting with ECharts.
   */
  chartApiRef?: React.MutableRefObject<ChartApi | undefined>;
  syncGroup?: string;
  onChartInitialized?: (instance: ECharts) => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

function errorOnMediaOption(option: EChartsCoreOption) {
  if (option && option.media) {
    throwDevError(
      'The `media` propert of the ECharts `option` is not supported. See the guidelines for more information: https://viz-framework.chronosphere.io/?path=/docs/documentation-for-engineers-for-contributors-visualization-guidelines--docs#working-with-echarts'
    );
  }
}

/**
 * Binds a custom event handler to the chart.
 */
function useEChartEvent<T>(
  chartRef: React.RefObject<ECharts | null>,
  eventName: EventName,
  onEventFunction?: OnEventFunction<T> | OnBatchEventFunction | (() => void)
) {
  const prevHandlerRef = useRef<typeof onEventFunction>();

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (prevHandlerRef.current !== onEventFunction) {
      chart.off(eventName);
      if (onEventFunction) {
        if (isEChartMouseEvent(eventName)) {
          chart.on(eventName, (params) =>
            (onEventFunction as OnEventFunction<T>)(params as MouseEventsParameters<T>, chart)
          );
        } else if (isEChartBatchEvent(eventName)) {
          chart.on(eventName, (params) => {
            (onEventFunction as OnBatchEventFunction)(params as BatchEventsParameters);
          });
        } else {
          chart.on(eventName, () => (onEventFunction as () => void)());
        }
      }
      prevHandlerRef.current = onEventFunction;
    }
  }, [chartRef, eventName, onEventFunction]);
}

const disableTouchStartPropagation: React.TouchEventHandler<HTMLDivElement> = (e) => {
  // Stop propagation, so that the data zoom feature in ECharts never gets this touch event. We intentionally disable
  // that feature in touch-based environments because it is very easy for consumers to accidentally zoom while scrolling
  // or otherwise interacting with the application, and it is frustrating.
  // TODO: see if this can be removed when the fix in https://github.com/apache/echarts/pull/21068 is released.
  e.stopPropagation();
};

const EChartWithoutMemo = function EChart<SeriesData>({
  option,
  theme,
  renderer = 'canvas',
  width,
  height,
  onMouseover,
  onMouseout,
  onClick,
  onDataZoom,
  onFinished,
  chartApiRef,
  syncGroup,
  onChartInitialized,
  'aria-label': ariaLabel,
  'data-testid': dataTestid,
}: EChartsProps<SeriesData>) {
  const initialOption = useRef<EChartsCoreOption>(option);
  const prevOption = useRef<EChartsCoreOption>(option);
  const prevTheme = useRef<string | EChartsTheme | undefined>(theme);
  const prevHeight = useRef(height);
  const prevWidth = useRef(width);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartElement = useRef<ECharts | null>(null);
  const _instance = useRef<ECharts>();

  // Initialize chart, dispose on unmount
  useLayoutEffect(() => {
    if (containerRef.current === null || chartElement.current !== null) return;
    chartElement.current = init(containerRef.current, theme, { renderer: renderer });
    if (chartElement.current === undefined) return;

    errorOnMediaOption(initialOption.current);
    chartElement.current.setOption(initialOption.current, true);

    onChartInitialized?.(chartElement.current);
    if (_instance !== undefined) {
      _instance.current = chartElement.current;
    }
    return () => {
      if (chartElement.current === null) return;
      chartElement.current.dispose();
      chartElement.current = null;

      if (_instance?.current) {
        _instance.current = undefined;
      }
    };
  }, [_instance, onChartInitialized, theme, renderer]);

  useImperativeHandle(chartApiRef, () => {
    return {
      getContainerEl: () => {
        // We use this instead of having people call the `getDom` method on the echarts instance
        // because that significantly increases the risk of memory leaks associated with accidentally
        // holding the large, complex echarts instance in closures.
        return containerRef.current;
      },
      getChartModel: () => {
        return _instance?.current?.['_model'];
      },
      dispatchAction: (payload: Payload) => {
        _instance?.current?.dispatchAction(payload);
      },
      convertFromPixel: (finder, value) => {
        return _instance?.current?.convertFromPixel(finder, value);
      },
      containPixel: (finder, value) => {
        return _instance?.current?.containPixel(finder, value);
      },
      convertToPixel: (finder, value) => {
        return _instance?.current?.convertToPixel(finder, value);
      },
      showLoading: () => {
        _instance?.current?.showLoading();
      },
      hideLoading: () => {
        _instance?.current?.hideLoading();
      },
      on: (event, handler) => {
        _instance?.current?.on(event, handler);
      },
      resize: () => {
        _instance?.current?.resize();
      },
    };
  }, []);

  // When syncGroup is explicitly set, charts within same group share interactions such as crosshair
  useEffect(() => {
    if (chartElement.current !== null && chartElement.current !== undefined) {
      if (syncGroup === undefined) {
        // Reset off-screen chart to not share crosshair with inView charts
        chartElement.current.group = '';
      } else {
        // When syncGroup is passed, sync crosshair for all charts with matching id
        chartElement.current.group = syncGroup;
        connect(syncGroup); // more info: https://echarts.apache.org/en/api.html#echarts.connect
      }
    }
  }, [syncGroup, chartElement]);

  // Update chart data when option changes
  useEffect(() => {
    if (!prevOption.current || !chartElement.current || !option) {
      return;
    }
    const optionArgs = generateOptionUpdateArgs(prevOption.current, option, {
      themeChanged: theme !== prevTheme.current,
    });
    if (!optionArgs) {
      // Nothing meaningfully changed. Do not update option.
      return;
    }

    // Set the `DEBUG_ECHARTS_OPTION_UPDATES` env var to `true` in development mode
    // to temporarily debug which top-level options are being changed within ECharts.
    // This can be helpful for debugging use cases where adjustments to memoization
    // or other configuration are needed.
    if (shouldDebugOptionUpdates()) {
      // eslint-disable-next-line no-console
      console.log(...debugOptionUpdateArgs(optionArgs));
    }

    errorOnMediaOption(optionArgs.option);
    chartElement.current.setOption(optionArgs.option, optionArgs.opts);

    // Save the whole option as prevOption, NOT `optionArgs.option`, which is
    // a partial option that just includes values that changed.
    prevOption.current = option;

    prevTheme.current = theme;
  }, [option, theme]);

  // Bind custom event handlers to the chart
  useEChartEvent(chartElement, 'mouseover', onMouseover);
  useEChartEvent(chartElement, 'mouseout', onMouseout);
  useEChartEvent(chartElement, 'click', onClick);
  useEChartEvent(chartElement, 'datazoom', onDataZoom);
  useEChartEvent(chartElement, 'finished', onFinished);

  // Throttled resize without a specific timeout uses `requestAnimationFrame`,
  // to determine when to trigger resizes, which will hopefully lead to a result
  // that is a bit smoother for users.
  const throttledUpdateSize = useThrottle(() => {
    if (!chartElement.current) return;
    chartElement.current.resize();
  });

  useEffect(() => {
    // Do not try to resize if we already rendered at these dimensions because
    // resizing is expensive. This also helps prevent a resize on first load
    // of the chart, when it is initialized with the correct dimensions already.
    if (width === prevWidth.current && height === prevHeight.current) {
      return;
    }

    throttledUpdateSize();
    prevWidth.current = width;
    prevHeight.current = height;
  }, [width, height, throttledUpdateSize]);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        height,
      }}
      // Disable propagation of touch events. Note that this is intentionally called on the "capture" phase to ensure
      // this event gets called before the event on the canvas in ECharts, so stopping the propagation works.
      onTouchStartCapture={disableTouchStartPropagation}
      aria-label={ariaLabel}
      data-testid={dataTestid}
      // Attr used by visual testing to make it easy to validate canvas changes are stable.
      data-chart-renderer={renderer}
    ></div>
  );
};

// Doing this separately instead of wrapping the original function definition
// to more easily get the types with generics working properly.
/**
 * This component is a React wrapper around the [ECharts library](https://echarts.apache.org/),
 * which is a core building block of the visualization framework.
 *
 * This component should only be used for creating new visualizations within the framework. New use
 * of this component outside of the framework is strongly discouraged and enforced via eslint rules
 * in `cloud`.
 *
 * See [the contributor guidelines](?path=/docs/documentation-for-engineers-for-contributors-visualization-guidelines--docs#working-with-echarts)
 * for additional guidelines about working with ECharts and this component.
 */
export const EChart = React.memo(function EChart<SeriesData>(props: EChartsProps<SeriesData>) {
  const MockEChart = getMockEChart();
  if (MockEChart !== undefined && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/static-components
    return <MockEChart {...props} />;
  }

  return <EChartWithoutMemo {...props} />;
}) as typeof EChartWithoutMemo;

export type EChartComponent = typeof EChart;
